import { Button, Dropdown, Input, Popover, Spin, Typography } from "antd";
import { ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { TbCheck, TbChevronDown, TbSearch, TbUserCircle } from "react-icons/tb";
import { IconType } from "react-icons";
import { ApiService } from "api/api.service";
import { MemberKind, MemberRole, MemberTarget, TaskItemMember } from "dtos/tasks/task-template.dto";
import { RoleTypes } from "dtos/user.dto";
import { TEAM_ROLES } from "pages/ApplicationItem/TeamSection";
import { taskUi } from "components/TaskBoard/design";

/** Sent as assigneeRole when the slot is the client. Must match what the runtime resolves. */
export const CLIENT_ROLE = "client";

export type MemberSelection = {
  kind: MemberKind;
  target: MemberTarget;
  assigneeRole?: string;
  assigneeId?: string;
};

type Scope = "all" | "role" | "person" | "external";

type Entry =
  | { type: "role"; id: string; label: string; icon?: IconType; color?: string }
  | { type: "external"; id: string; label: string }
  | { type: "person"; id: string; label: string; email?: string; orgRole?: string };

const ROLE_LABEL: Record<MemberRole, string> = {
  [MemberRole.OWNER]: "Owner",
  [MemberRole.VERIFIER]: "Verifier",
  [MemberRole.ACTOR]: "Actor",
};

const ROLE_CAPTION: Record<MemberRole, string> = {
  [MemberRole.OWNER]: "Accountable for this task",
  [MemberRole.VERIFIER]: "Signs the work off",
  [MemberRole.ACTOR]: "Does the work and records the outcome",
};

const TYPE_HINT: Record<Entry["type"], string> = {
  role: "team role",
  external: "external",
  person: "",
};

const ORG_ROLES = Object.values(RoleTypes);

const PAGE = 6;

export const getInitials = (name: string) =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? "")
    .join("");

const highlight = (text: string, term: string): ReactNode => {
  if (!term) return text;
  const at = text.toLowerCase().indexOf(term.toLowerCase());
  if (at < 0) return text;

  return (
    <>
      {text.slice(0, at)}
      <mark style={{ background: "#fdf0b8", borderRadius: 2, padding: "0 1px", color: "inherit" }}>
        {text.slice(at, at + term.length)}
      </mark>
      {text.slice(at + term.length)}
    </>
  );
};

const EntryAvatar = ({ entry }: { entry: Entry }) => {
  const base = {
    width: 26,
    height: 26,
    borderRadius: "50%",
    fontSize: 10,
    fontWeight: 650,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  } as const;

  if (entry.type === "person") {
    return (
      <span
        style={{
          ...base,
          background: taskUi.indigoSoft,
          border: `1px solid ${taskUi.indigoLine}`,
          color: taskUi.indigo,
        }}
      >
        {getInitials(entry.label)}
      </span>
    );
  }

  if (entry.type === "external") {
    return (
      <span style={{ ...base, background: "#fdf3e7", border: "1px solid #ebd8ac", color: "#97551f" }}>
        <TbUserCircle size={14} />
      </span>
    );
  }

  const Icon = entry.icon;

  return (
    <span
      style={{
        ...base,
        background: entry.color ? `${entry.color}1a` : "#f4f4f8",
        border: `1px solid ${entry.color ?? "#e5e7eb"}`,
        color: entry.color ?? "#6b6b76",
      }}
    >
      {Icon ? <Icon size={13} /> : getInitials(entry.label)}
    </span>
  );
};

const ScopeChip = ({
  active,
  count,
  onClick,
  children,
  disabled = false,
}: {
  active: boolean;
  count?: number;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) => (
  <Button
    disabled={disabled}
    size="small"
    shape="round"
    type={active ? "primary" : "default"}
    className="disabled:bg-red-50 disabled:border-red-300 disabled:text-red-500"
    onClick={onClick}
  >
    {children}
    {count !== undefined && (
      <span style={{ marginLeft: 5, opacity: 0.65, fontVariantNumeric: "tabular-nums" }}>{count}</span>
    )}
  </Button>
);

interface MemberPickerProps {
  role: MemberRole;
  member?: TaskItemMember;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPick: (selection: MemberSelection) => void;
  onClear: () => void;
  saving?: boolean;
  /** Code of the previous task, when it has a member in this same slot. */
  copyFrom?: { code: string; member: TaskItemMember };
  children: ReactNode;
}

export const MemberPicker = ({
  role,
  member,
  open,
  onOpenChange,
  onPick,
  onClear,
  saving,
  copyFrom,
  children,
}: MemberPickerProps) => {
  const [query, setQuery] = useState("");
  const [term, setTerm] = useState("");
  const [scope, setScope] = useState<Scope>("all");
  const [orgRole, setOrgRole] = useState<RoleTypes | undefined>();
  const [limit, setLimit] = useState(PAGE);
  const [active, setActive] = useState(0);
  const rowRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setTerm(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (open) return;
    setQuery("");
    setTerm("");
    setScope("all");
    setOrgRole(undefined);
    setLimit(PAGE);
    setActive(0);
  }, [open]);

  const wantsPeople = scope === "all" || scope === "person";

  const people = ApiService.user.useGetPaginated({
    page: 1,
    limit,
    search: term,
    role: orgRole ? [orgRole] : [],
  });

  const roleEntries = useMemo<Entry[]>(
    () =>
      TEAM_ROLES.filter((option) => option.label.toLowerCase().includes(term.toLowerCase())).map((option) => ({
        type: "role" as const,
        id: option.value,
        label: option.label,
        icon: option.icon,
        color: option.color,
      })),
    [term],
  );

  const externalEntries = useMemo<Entry[]>(
    () =>
      "the client".includes(term.toLowerCase())
        ? [{ type: "external" as const, id: CLIENT_ROLE, label: "The client" }]
        : [],
    [term],
  );

  const peopleEntries = useMemo<Entry[]>(
    () =>
      (people.data?.result.data ?? []).map((user) => ({
        type: "person" as const,
        id: String(user._id),
        label: user.name,
        email: user.email,
        orgRole: user.role,
      })),
    [people.data],
  );

  const entries = useMemo<Entry[]>(() => {
    if (scope === "role") return role === MemberRole.OWNER ? [] : roleEntries;
    if (scope === "external") return externalEntries;
    if (scope === "person") return peopleEntries;

    return term
      ? [...peopleEntries, ...(role === MemberRole.OWNER ? [] : roleEntries), ...externalEntries]
      : [...(role === MemberRole.OWNER ? [] : roleEntries), ...externalEntries, ...peopleEntries];
  }, [scope, term, roleEntries, externalEntries, peopleEntries]);

  useEffect(() => setActive(0), [term, scope, orgRole]);

  const selectionFor = (entry: Entry): MemberSelection => {
    if (entry.type === "person") {
      return { kind: MemberKind.INTERNAL, target: MemberTarget.INDIVIDUAL, assigneeId: entry.id };
    }
    if (entry.type === "role") {
      return { kind: MemberKind.INTERNAL, target: MemberTarget.ROLE, assigneeRole: entry.id };
    }
    return { kind: MemberKind.EXTERNAL, target: MemberTarget.ROLE, assigneeRole: CLIENT_ROLE };
  };

  const isCurrent = (entry: Entry) => {
    if (!member) return false;
    if (entry.type === "person") return String(member.assigneeId ?? "") === entry.id;
    return member.assigneeRole === entry.id;
  };

  const move = (delta: number) =>
    setActive((prev) => {
      const next = Math.min(Math.max(prev + delta, 0), Math.max(entries.length - 1, 0));
      rowRefs.current[next]?.scrollIntoView({ block: "nearest" });
      return next;
    });

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      move(1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      move(-1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const entry = entries[active];
      if (entry) onPick(selectionFor(entry));
    } else if (event.key === "Escape") {
      onOpenChange(false);
    }
  };

  const currentLabel = () => {
    if (!member) return "";
    if (member.target === MemberTarget.ROLE) {
      if (member.assigneeRole === CLIENT_ROLE) return "The client";
      return TEAM_ROLES.find((option) => option.value === member.assigneeRole)?.label ?? member.assigneeRole ?? "";
    }
    return peopleEntries.find((entry) => entry.id === String(member.assigneeId))?.label ?? "A person";
  };

  const showPeopleSpinner = wantsPeople && people.isFetching && peopleEntries.length === 0;
  const showMorePeople = wantsPeople && peopleEntries.length >= limit;

  const content = (
    <div style={{ width: 372 }}>
      <div style={{ padding: "12px 16px 10px", borderBottom: "1px solid #f0f0f4" }}>
        <Typography.Text strong style={{ fontSize: 13.5, display: "block" }}>
          {ROLE_LABEL[role]}
        </Typography.Text>
        <Typography.Text style={{ fontSize: 11.5, color: taskUi.textFaint }}>{ROLE_CAPTION[role]}</Typography.Text>
      </div>

      <div className="flex flex-col gap-2.5" style={{ padding: "12px 16px" }}>
        <Input
          autoFocus
          allowClear
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          prefix={<TbSearch size={14} color="#90909c" />}
          placeholder="Search team roles and people"
        />

        <div className="flex flex-wrap items-center gap-1.5">
          <ScopeChip active={scope === "all"} onClick={() => setScope("all")}>
            All
          </ScopeChip>
          <ScopeChip
            disabled={role === MemberRole.OWNER}
            active={scope === "role"}
            count={roleEntries.length}
            onClick={() => setScope("role")}
          >
            Roles
          </ScopeChip>
          <ScopeChip
            active={scope === "person"}
            count={people.data?.result.totalObjects}
            onClick={() => setScope("person")}
          >
            People
          </ScopeChip>
          <ScopeChip active={scope === "external"} count={externalEntries.length} onClick={() => setScope("external")}>
            External
          </ScopeChip>

          {/* {scope === "person" && (
            <Dropdown
              trigger={["click"]}
              menu={{
                items: [
                  { key: "any", label: "All org roles", onClick: () => setOrgRole(undefined) },
                  ...ORG_ROLES.map((value) => ({ key: value, label: value, onClick: () => setOrgRole(value) })),
                ],
              }}
            >
              <Button size="small" shape="round" type={orgRole ? "primary" : "default"} ghost={Boolean(orgRole)}>
                {orgRole ?? "All org roles"} <TbChevronDown size={11} />
              </Button>
            </Dropdown>
          )} */}
        </div>

        {!term && copyFrom && (
          <Button
            size="small"
            shape="round"
            style={{ alignSelf: "flex-start" }}
            onClick={() =>
              onPick({
                kind: copyFrom.member.kind,
                target: copyFrom.member.target,
                assigneeRole: copyFrom.member.assigneeRole,
                assigneeId: copyFrom.member.assigneeId ? String(copyFrom.member.assigneeId) : undefined,
              })
            }
          >
            Same as {copyFrom.code}
          </Button>
        )}
      </div>

      {member && (
        <div
          className="flex items-center gap-2.5 border-0 border-b border-t border-solid"
          style={{ height: 42, padding: "0 16px", background: taskUi.subtle, borderColor: "#f0f0f4" }}
        >
          <Typography.Text style={{ fontSize: 11, color: taskUi.textFaint, width: 48 }}>Current</Typography.Text>
          <Typography.Text style={{ fontSize: 12.5, flexGrow: 1 }} ellipsis>
            {currentLabel()}
          </Typography.Text>
          <Button size="small" type="text" danger loading={saving} onClick={onClear}>
            Clear
          </Button>
        </div>
      )}

      <div style={{ maxHeight: 320, overflowY: "auto" }}>
        {showPeopleSpinner && entries.length === 0 ? (
          <div className="flex justify-center" style={{ padding: 20 }}>
            <Spin size="small" />
          </div>
        ) : entries.length === 0 ? (
          <div style={{ padding: "14px 16px" }}>
            <Typography.Text style={{ fontSize: 12.5, color: taskUi.textFaint }}>
              {term
                ? `Nothing matches “${term}”${orgRole ? ` among ${orgRole}` : ""}. A team role keeps working when the case team changes.`
                : "Nothing to show in this filter."}
            </Typography.Text>
          </div>
        ) : (
          entries.map((entry, i) => (
            <button
              key={`${entry.type}-${entry.id}`}
              ref={(node) => (rowRefs.current[i] = node)}
              type="button"
              onClick={() => onPick(selectionFor(entry))}
              onMouseEnter={() => setActive(i)}
              className="flex w-full items-center gap-2.5 border-0 text-left"
              style={{
                height: 44,
                padding: "0 16px",
                cursor: "pointer",
                background: i === active ? "#f7f6fe" : "transparent",
                boxShadow: i === active ? `inset 3px 0 0 ${taskUi.indigo}` : undefined,
              }}
            >
              <EntryAvatar entry={entry} />
              <span className="flex min-w-0 flex-grow flex-col">
                <Typography.Text style={{ fontSize: 13 }} ellipsis>
                  {highlight(entry.label, term)}
                </Typography.Text>
                {entry.type === "person" && (entry.orgRole || entry.email) && (
                  <Typography.Text style={{ fontSize: 11, color: taskUi.textFaint }} ellipsis>
                    {[entry.orgRole, entry.email].filter(Boolean).join(" · ")}
                  </Typography.Text>
                )}
              </span>
              {scope === "all" && TYPE_HINT[entry.type] && (
                <Typography.Text style={{ fontSize: 10.5, color: "#b4b4be" }}>{TYPE_HINT[entry.type]}</Typography.Text>
              )}
              {isCurrent(entry) && <TbCheck size={16} color={taskUi.indigo} />}
            </button>
          ))
        )}

        {showMorePeople && (
          <div style={{ padding: "4px 16px 10px" }}>
            <Button type="link" size="small" style={{ padding: 0 }} onClick={() => setLimit((l) => l + PAGE)}>
              Show more people
            </Button>
          </div>
        )}
      </div>

      <div
        className="flex items-center gap-2 border-0 border-t border-solid"
        style={{ padding: "10px 16px", borderColor: "#f0f0f4", background: "#fcfcfd" }}
      >
        <Typography.Text style={{ fontSize: 11.5, color: taskUi.textFaint, flexGrow: 1 }}>
          {term ? "↑ ↓ to move · Enter to pick" : "Type a name to search everyone"}
        </Typography.Text>
        <Typography.Text style={{ fontSize: 11, color: "#b4b4be" }}>
          {saving ? "Saving…" : "Saves as you pick"}
        </Typography.Text>
      </div>
    </div>
  );

  return (
    <Popover open={open} onOpenChange={onOpenChange} trigger="click" placement="leftTop" content={content}>
      {children}
    </Popover>
  );
};
